import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { projectReportColumns } from "../../pages/report/constants";
import { AnalyticsService } from "../../services";
import { get_date } from "../generalComponets";
import { CardLayout, MTable } from "../v3";
import { downloadFile } from "../../utils/download.utils";

export const ProjectReportResults = ({
  reportType,
  data = {},
  loading = false,
  paginationState,
  handlePaginationChange = () => {},
  reportFilters = {},
}) => {
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [prevFilters, setPrevFilters] = useState({});

  useEffect(() => {
    if (pdfSuccess && JSON.stringify(reportFilters) !== JSON.stringify(prevFilters)) {
      setPdfSuccess(false);
    }
  }, [reportFilters, prevFilters, pdfSuccess]);

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    setPdfSuccess(false);

    try {
      const response = await AnalyticsService.downloadProjectReportPdf(reportFilters || {});
      const currentDateTime = moment().format("YYYYMMDD-HHmmss");
      const filename = `project-report-${reportType}-${currentDateTime}.pdf`;

      downloadFile(response.data, filename);
      toast.success("Report downloaded successfully");

      setPdfSuccess(true);
      setPrevFilters(reportFilters);
    } catch (error) {
      console.error("Report download failed:", error);
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to download report");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const rows = data?.rows?.map((row, index) => {
    const isExpense = row?.entryType === "expense";

    return {
      id: index + 1 + paginationState.page * paginationState.pageSize,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      formattedDate: get_date(row.date),
      income: isExpense ? "-" : row.amount,
      expense: isExpense ? row.amount : "-",
      ...row,
    };
  });

  const totalOpeningBalance = rows.reduce((sum, row) => sum + Number(row.openingBalance || 0), 0);
  const totalWorkAmount = rows.reduce((sum, row) => sum + Number(row.totalWork || 0), 0);
  const totalPurchaseAmount = rows.reduce((sum, row) => sum + Number(row.totalPurchase || 0), 0);
  const totalExpense = rows.reduce((sum, row) => sum + Number(row.totalOtherExpense || 0), 0);
  const totalIncomeAmount = rows.reduce((sum, row) => sum + Number(row.totalIncome || 0), 0);
  const totalClosingBalance = rows.reduce((sum, row) => sum + Number(row.closingBalance || 0), 0);
  const totalCashIn = rows.reduce((sum, row) => sum + (row.entryType === "income" ? Number(row.amount || 0) : 0), 0);
  const totalCashOut = rows.reduce((sum, row) => sum + (row.entryType === "expense" ? Number(row.amount || 0) : 0), 0);

  const subtotal =
    data?.count > 0
      ? [
          {
            id: "subtotal",
            label: "Subtotal",
            totalOpeningBalance: totalOpeningBalance.toFixed(2),
            totalWorkAmount: totalWorkAmount.toFixed(2),
            totalPurchaseAmount: totalPurchaseAmount.toFixed(2),
            totalExpense: totalExpense.toFixed(2),
            totalIncomeAmount: totalIncomeAmount.toFixed(2),
            totalClosingBalance: totalClosingBalance.toFixed(2),
            totalCashIn: totalCashIn.toFixed(2),
            totalCashOut: totalCashOut.toFixed(2),
          },
        ]
      : [];

  const allRows = [...rows, ...subtotal];

  return (
    <CardLayout>
      <MTable
        // Export data
        showExportButton // TODO: implement permission
        isPdfGenerating={isPdfGenerating}
        pdfSuccess={pdfSuccess}
        currentFilters={reportFilters}
        prevFilters={prevFilters}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={projectReportColumns[reportType]}
        tableRows={allRows}
        rowCount={data.count}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
